import { BaseReport, type ReportRunDeps, type SortAccessor } from './baseReport.js';
import type { ReportCache } from './reportCache.js';
import type { ApiClient } from './runtime.js';
import type { CampaignRow, JourneyMetricKey, ReportDom } from './types.js';
import { csvVal, downloadCsv, esc, formatHeader, isGroupLast } from './utils.js';

const CAMPAIGN_METRIC_KEYS: readonly JourneyMetricKey[] = [
  'started',
  'activated',
  'messaged',
  'converted',
  'finished',
  'exited_early',
  'never_activated',
];

export class CampaignJourneyReport extends BaseReport<CampaignRow> {
  private filterValue = '';
  private hideZero = true;
  private hours = 4;

  constructor(cache: ReportCache, apiClient: ApiClient, dom: ReportDom, deps: ReportRunDeps) {
    super('campaign-journey', cache, apiClient, dom, deps);
  }

  override get reportLabel(): string {
    return 'Campaign Journey';
  }

  setHours(hours: number): void {
    this.hours = hours;
  }

  getHours(): number {
    return this.hours;
  }

  protected override buildCacheKey(slugs: string[]): string {
    return `campaign-journey|${[...slugs].sort().join(',')}|${this.hours}`;
  }

  protected override fetchData(slugs: string[]): Promise<CampaignRow[]> {
    return this.apiClient.getCampaignJourney(slugs, this.hours);
  }

  protected override resetFilterState(): void {
    this.filterValue = '';
    this.hideZero = true;
  }

  protected override peopleAccessor(): (r: CampaignRow) => number {
    return (r) => r.journey_metrics?.started ?? 0;
  }

  protected override channelAccessor(): (r: CampaignRow) => string {
    return (r) => r.channel;
  }

  protected override summaryItemLabel(): string {
    return 'num_campaigns';
  }

  protected override filterRows(rows: CampaignRow[]): CampaignRow[] {
    return rows.filter((r) => {
      if (!(r.campaign_name || '').toLowerCase().includes(this.filterValue)) return false;
      if (this.hideZero && isAllZero(r)) return false;
      return true;
    });
  }

  protected override renderTitle(rows: CampaignRow[], filtered: CampaignRow[]): void {
    const totalStarted = filtered.reduce(
      (sum, r) => sum + (r.journey_metrics?.started ?? 0),
      0,
    );
    this.dom.title.innerHTML =
      `Campaign Journey Report  —  last ${this.hours}h  —  ${filtered.length} of ${rows.length} active campaign${rows.length !== 1 ? 's' : ''} ` +
      `<span class="title-people">(${totalStarted.toLocaleString()} people started)</span>` +
      this.reportTimestampHtml();
  }

  protected override renderToolbar(toolbar: HTMLElement): void {
    toolbar.innerHTML = `
      <input type="text" id="campaign-filter" placeholder="Filter by campaign name…" />
      <label class="toggle-label">
        <input type="checkbox" id="hide-zero-campaign-journey-toggle" ${this.hideZero ? 'checked' : ''} />
        Hide zero counts
      </label>
    `;

    const filterEl = toolbar.querySelector<HTMLInputElement>('#campaign-filter')!;
    filterEl.value = this.filterValue;
    filterEl.addEventListener('input', () => {
      this.filterValue = filterEl.value.toLowerCase();
      this.refreshView();
    });

    const zeroToggle = toolbar.querySelector<HTMLInputElement>('#hide-zero-campaign-journey-toggle')!;
    zeroToggle.addEventListener('change', () => {
      this.hideZero = zeroToggle.checked;
      this.refreshView();
    });
  }

  protected override renderData(area: HTMLElement, rows: CampaignRow[], filtered: CampaignRow[]): void {
    this.appendClientSummary(area, filtered);

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.className = 'empty-msg';
      msg.textContent =
        rows.length === 0
          ? 'No active campaigns found for the selected clients.'
          : 'No campaigns match your filter.';
      area.appendChild(msg);
      return;
    }

    const sorted = this.sortRows(filtered, campaignAccessor);
    const windowEnd = this.lastCachedAt ?? this.lastRanAt ?? new Date();

    const table = document.createElement('table');
    table.innerHTML = `
      <thead>
        <tr>
          ${this.thSortable('client_slug', 'Client')}
          ${this.thSortable('campaign_id', 'Campaign ID', 'right')}
          ${this.thSortable('campaign_name', 'Campaign')}
          ${this.thSortable('channel', 'Channel')}
          ${CAMPAIGN_METRIC_KEYS.map((k) => this.thSortable(k, formatHeader(k), 'right')).join('')}
        </tr>
      </thead>
      <tbody>
        ${sorted
          .map((r, i) => {
            const url = campaignMetricsUrl(r.workspace_id, r.campaign_id, this.hours, windowEnd);
            return `
          <tr${isGroupLast(sorted, i, 'client_slug') ? ' class="client-group-last"' : ''}>
            <td>${esc(r.client_slug)}</td>
            <td class="num"><a href="${esc(url)}" target="_blank" rel="noopener">${esc(r.campaign_id)}</a></td>
            <td><a href="${esc(url)}" target="_blank" rel="noopener">${esc(r.campaign_name)}</a></td>
            <td>${esc(r.channel)}</td>
            ${CAMPAIGN_METRIC_KEYS.map(
              (k) => `<td class="num">${(r.journey_metrics?.[k] ?? 0).toLocaleString()}</td>`,
            ).join('')}
          </tr>`;
          })
          .join('')}
      </tbody>`;

    area.appendChild(table);
    this.bindSortHeaders(table);
  }

  override exportCsv(): void {
    if (!this.lastData || this.lastData.length === 0) return;
    const filtered = this.filterRows(this.lastData);
    const visible = this.sortRows(filtered, campaignAccessor);
    const windowEnd = this.lastCachedAt ?? this.lastRanAt ?? new Date();

    let csv = `Client,Campaign ID,Campaign,Channel,${CAMPAIGN_METRIC_KEYS.map(formatHeader).join(',')},Link\n`;
    csv += visible
      .map(
        (r) =>
          `${csvVal(r.client_slug)},${r.campaign_id},${csvVal(r.campaign_name)},${csvVal(r.channel)},${CAMPAIGN_METRIC_KEYS.map((k) => r.journey_metrics?.[k] ?? 0).join(',')},${csvVal(campaignMetricsUrl(r.workspace_id, r.campaign_id, this.hours, windowEnd))}`,
      )
      .join('\n');

    downloadCsv(csv, this.reportType);
  }
}

function isAllZero(r: CampaignRow): boolean {
  return CAMPAIGN_METRIC_KEYS.every((k) => (r.journey_metrics?.[k] ?? 0) === 0);
}

function campaignMetricsUrl(
  workspaceId: number | string,
  campaignId: number,
  hours: number,
  windowEnd: Date,
): string {
  const end = windowEnd;
  const start = new Date(end.getTime() - hours * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const params = new URLSearchParams({
    end_time: fmt(end),
    message_channels: 'email_twilio_webhook_slack',
    pd_channel: 'email',
    start_time: fmt(start),
    tab: 'journey',
    unit: 'count',
  });
  return `https://fly.customer.io/workspaces/${encodeURIComponent(String(workspaceId))}/journeys/campaigns/${encodeURIComponent(String(campaignId))}/overview/metrics?${params.toString()}`;
}

const campaignAccessor: SortAccessor<CampaignRow> = (key) => {
  if (key === 'client_slug') return (r) => r.client_slug;
  if (key === 'campaign_id') return (r) => r.campaign_id;
  if (key === 'campaign_name') return (r) => r.campaign_name;
  if (key === 'channel') return (r) => r.channel;
  if ((CAMPAIGN_METRIC_KEYS as readonly string[]).includes(key)) {
    return (r) => r.journey_metrics?.[key as JourneyMetricKey] ?? 0;
  }
  return () => '';
};
